import React, { useEffect, useState } from 'react';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import { getAllowedIpAddrs, saveAllowedIpAddrs } from 'admin/actions/organization';
import { useAppDispatch, useAppSelector } from 'admin/hooks/useApp';
import { openAlertModal } from 'admin/actions/alert-modal';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import ToggleSwitch from 'admin/components/atoms/ToggleSwitch';
import Button from 'admin/components/atoms/Buttons';
import FontAwesome from 'admin/components/atoms/FontAwesome';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '各種設定', href: '', isCurrentPage: true },
  { value: 'システム設定', href: '', isCurrentPage: true },
];

type IpEntry = {
  id: number;
  ip: string;
  description: string;
};

let nextId = 1;
const makeEntry = (ip = '', description = ''): IpEntry => ({ id: nextId++, ip, description });

const Grid = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 24px;
  margin-top: 20px;
`;

const PageHeader = styled.h1`
  font-size: ${({ theme }) => theme.typography.heading3.bold.fontSize};
  font-weight: ${({ theme }) => theme.typography.heading3.bold.fontWeight};
  line-height: ${({ theme }) => theme.typography.heading3.bold.lineHeight};
  color: ${({ theme }) => theme.colors.gray['400']};
`;

const IpRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 32px 32px;
  gap: 10px;
  align-items: center;
`;

const StyledInput = styled.input<{ disabled?: boolean }>`
  width: 100%;
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.gray['200']};
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
  background: ${({ disabled, theme }) => (disabled ? theme.colors.gray['50'] : theme.colors.white)};
  color: ${({ disabled, theme }) =>
    disabled ? theme.colors.gray['200'] : theme.colors.gray['500']};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};

  &:focus {
    border-color: ${({ disabled, theme }) =>
      disabled ? theme.colors.gray['200'] : theme.colors.gray['400']};
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TrashButton = styled(IconButton)<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ disabled, theme }) =>
    disabled ? theme.colors.gray['200'] : theme.colors.gray['300']};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover {
    color: ${({ theme }) => theme.colors.red['300']};
  }
`;

const PlusButton = styled(IconButton)<{ disabled?: boolean }>`
  color: ${({ disabled, theme }) =>
    disabled ? theme.colors.gray['200'] : theme.colors.gray['400']};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.green['500']};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.gray['100']};
`;

const ValidationError = styled.div`
  color: ${({ theme }) => theme.colors.red['400']};
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.red['400']};
  margin-left: 2px;
`;

const ModalWarningMessage = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;

  p {
    margin-bottom: 0;
    color: ${({ theme }) => theme.colors.red['300']};
  }
`;

const System = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const orgCode = user.organization?.code;

  const [ipRestriction, setIpRestriction] = useState(false);
  const [entries, setEntries] = useState<IpEntry[]>([]);
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  const [currentIp, setCurrentIp] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgCode) return;
    getAllowedIpAddrs(orgCode).then((data) => {
      setIpRestriction(data.ip_restriction);
      setCurrentIp(data.current_ip ?? '');
      const loaded = data.allowed_ips.map((a) => makeEntry(a.ip, a.description));
      setEntries(loaded.length > 0 ? loaded : [makeEntry()]);
      setSavedIds(new Set(loaded.map((e) => e.id)));
    });
  }, [orgCode]);

  const updateEntry = (id: number, field: 'ip' | 'description', value: string) => {
    setEntries((prev: IpEntry[]) =>
      prev.map((e: IpEntry) => (e.id === id ? { ...e, [field]: value } : e))
    );
  };

  const addEntry = () => setEntries((prev: IpEntry[]) => [...prev, makeEntry()]);

  const removeEntry = (id: number) => {
    setEntries((prev: IpEntry[]) => {
      const next = prev.filter((e: IpEntry) => e.id !== id);
      return next.length === 0 ? [makeEntry()] : next;
    });
  };

  const requestRemoveEntry = (id: number) => {
    const entry = entries.find((e: IpEntry) => e.id === id);
    removeEntry(id);
  };

  const handleSave = () => {
    if (!orgCode) return;

    if (ipRestriction) {
      const hasIncompleteEntry = entries.some(e => !e.ip.trim() || !e.description.trim());
      const hasAtLeastOneEntry = entries.length > 0;

      if (!hasAtLeastOneEntry || hasIncompleteEntry) {
        setValidationError('IPアドレス制限を設定してください');
        return;
      }

      const isCurrentIpIncluded = entries.some(e => e.ip.trim() === currentIp);
      if (currentIp && !isCurrentIpIncluded) {
        setValidationError('現在アクセスしているIPアドレスを入力してください');
        return;
      }
    }

    setValidationError(null);

    const valid = entries.filter((e: IpEntry) => e.ip.trim() && e.description.trim());
    const allowed_ips = valid.map(({ ip, description }) => ({
      ip: ip.trim(),
      description: description.trim(),
    }));

    setSaving(true);
    saveAllowedIpAddrs(orgCode, { ip_restriction: ipRestriction, allowed_ips })
      .then(() => {
        setSaving(false);
        setSaveSuccess(true);
        setSavedIds(new Set(valid.map((e: IpEntry) => e.id)));
        setEntries(valid.length > 0 ? valid : [makeEntry()]);
        setTimeout(() => setSaveSuccess(false), 3000);
      })
      .catch(() => setSaving(false));
  };

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      <PageHeader>システム設定</PageHeader>
      <Grid>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>IPアドレス制限</div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <ToggleSwitch checked={ipRestriction} onChange={setIpRestriction} showLabel={false} />
            <div>IPアドレス制限を有効にする</div>
          </div>

          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <IpRow>
              <div>
                IPアドレス<RequiredMark>*</RequiredMark>
              </div>
              <div>
                名称<RequiredMark>*</RequiredMark>
              </div>
            </IpRow>

            {entries.map((entry) => (
              <IpRow key={entry.id}>
                <StyledInput
                  value={entry.ip}
                  placeholder="例）192.168.0.1"
                  disabled={!ipRestriction || savedIds.has(entry.id)}
                  onChange={(e) => updateEntry(entry.id, 'ip', e.target.value)}
                />
                <StyledInput
                  value={entry.description}
                  placeholder="例）本社"
                  disabled={!ipRestriction}
                  maxLength={200}
                  onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                />
                <TrashButton
                  onClick={() => requestRemoveEntry(entry.id)}
                  type="button"
                  disabled={!ipRestriction}
                >
                  <FontAwesome icon="trash" />
                </TrashButton>
                <PlusButton type="button" disabled={!ipRestriction} onClick={addEntry}>
                  <FontAwesome icon="plus" />
                </PlusButton>
              </IpRow>
            ))}

            {validationError && <ValidationError>{validationError}</ValidationError>}
          </div>
        </div>
      </Grid>
      <Divider />
      <Alert color="success" isOpen={saveSuccess}>
        システム設定を保存しました
      </Alert>
      <Button onClick={handleSave} disabled={saving}>
        保存
      </Button>
    </>
  );
};

export default System;
